import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import type { useRequestClips } from "../../api/useRequestClips";
import { byBestThenTime, canRequestClip, isClipReady } from "../../model/clipState";
import type { HighlightsMatch, HighlightView } from "../../model/Highlight";
import { MatchHeadline } from "../HighlightParts";
import { MatchTimeline } from "./MatchTimeline";
import { MomentTile } from "./MomentTile";

const MAKE_ALL = msg`Make all · {count}`;
const ANALYSIS = msg`Match analysis`;
const ANALYZE = msg`Analyze the match`;
const FROM_ANALYSIS = msg`From the analysis`;

type MatchHighlightsProps = {
  // Матч с моментами под фильтром раздела.
  match: HighlightsMatch;
  clipsEnabled: boolean;
  requestClips: ReturnType<typeof useRequestClips>;
  // К этому матчу пришли из разбора или списка матчей — блок выделен.
  target: boolean;
  fromAnalysis: boolean;
  onOpen: (highlight: HighlightView) => void;
};

// Матч в разделе «Хайлайты» (доска 18д): шапка с исходом, K/D/A и переходом в разбор,
// «Сделать все», полоса матча с отметками и плитки моментов — лучший первым.
export const MatchHighlights = ({
  match,
  clipsEnabled,
  requestClips,
  target,
  fromAnalysis,
  onOpen,
}: MatchHighlightsProps) => {
  const { i18n } = useLingui();
  const headingId = useId();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const items = [...match.items].sort(byBestThenTime);
  const orderable = clipsEnabled ? items.filter(canRequestClip) : [];
  const { pendingIds } = requestClips;

  // Отметка на полосе: готовый клип открывается, остальные — выделяем плитку и прокручиваем
  // к ней (на телефоне плитки идут лентой вбок).
  const pick = (highlight: HighlightView) => {
    if (isClipReady(highlight)) {
      onOpen(highlight);
      return;
    }
    setSelectedId(highlight.id);
    document
      .getElementById(`moment-${highlight.id}`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  };

  return (
    <article
      id={`match-${match.match_id}`}
      aria-labelledby={headingId}
      className={cn(
        "@container flex scroll-mt-6 flex-col gap-3 rounded-[14px] border bg-surface p-4",
        target ? "border-accent ring-[3px] ring-accent/20" : "border-border",
      )}
    >
      <header className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
        <span className="min-w-0 flex-[999_1_240px]">
          <MatchHeadline
            match={match}
            headingId={headingId}
            extra={
              fromAnalysis && (
                <span className="rounded-full bg-accent px-2 py-px text-[11px] font-bold tracking-wide text-accent-foreground uppercase">
                  {i18n._(FROM_ANALYSIS)}
                </span>
              )
            }
          />
        </span>
        <span className="flex flex-[1_0_auto] flex-wrap justify-end gap-2">
          {orderable.length > 1 && (
            <Button
              className="h-11"
              loading={requestClips.isPending && orderable.every((h) => pendingIds.includes(h.id))}
              onClick={() => {
                requestClips.request(orderable.map((highlight) => highlight.id));
              }}
            >
              {i18n._({ ...MAKE_ALL, values: { count: orderable.length } })}
            </Button>
          )}
          <Button asChild variant="outline" className="h-11">
            <Link to="/matches/$matchId" params={{ matchId: match.match_id }}>
              {i18n._(match.analysis_id === null ? ANALYZE : ANALYSIS)}
              <ArrowRightIcon aria-hidden="true" />
            </Link>
          </Button>
        </span>
      </header>

      {match.duration_s !== null && match.duration_s > 0 && (
        <MatchTimeline
          durationS={match.duration_s}
          items={items}
          selectedId={selectedId}
          onPick={pick}
        />
      )}

      <ol className="grid snap-x snap-mandatory [scrollbar-width:thin] auto-cols-[76%] grid-flow-col gap-3 overflow-x-auto pb-1.5 @lg:auto-cols-auto @lg:grid-flow-row @lg:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] @lg:overflow-visible @lg:pb-0">
        {items.map((highlight) => (
          <MomentTile
            key={highlight.id}
            highlight={highlight}
            clipsEnabled={clipsEnabled}
            requesting={pendingIds.includes(highlight.id)}
            selected={selectedId === highlight.id}
            onOpen={() => {
              onOpen(highlight);
            }}
            onRequest={() => {
              setSelectedId(null);
              requestClips.request([highlight.id]);
            }}
          />
        ))}
      </ol>
    </article>
  );
};
